import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Maximize2, Sparkles, ArrowRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import SEO from '../components/SEO';

const getOptimizedImgUrl = (url, width = 800) => {
  if (url && url.includes('assets.auxodev.dk') && !url.includes('/cdn-cgi/image/')) {
    return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
  }
  return url;
};

export default function WineTastings() {
  const { t, getRouteUrl } = useLanguage();
  const page = t.tastingsPage;
  const [activeModalIndex, setActiveModalIndex] = useState(null);

  const galleryImages = page.galleryImages;

  const openLightbox = (index) => setActiveModalIndex(index);
  const closeLightbox = () => setActiveModalIndex(null);

  const prevImage = (e) => {
    e.stopPropagation();
    setActiveModalIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setActiveModalIndex((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <main className="bg-stone-50 pt-32">
      <SEO pageKey="tastings" />
      
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-[#f7f2ea] text-stone-900 border-b border-stone-200/80">
        <div className="grid-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-8 space-y-6">
              <span className="text-xs font-bold uppercase tracking-[0.4em] text-[#581c24] block">
                {page.badge}
              </span>
              <h1 className="text-5xl md:text-7xl font-serif font-bold uppercase leading-tight tracking-tight text-stone-950">
                {page.heroTitle1} <br /> 
                <span className="text-[#581c24] italic font-light">{page.heroTitle2}</span>
              </h1>
              <p className="text-base md:text-lg text-stone-700 max-w-xl leading-relaxed">
                {page.heroSubtitle}
              </p>
            </div>
            <div className="lg:col-span-4 flex justify-end">
              <div className="w-full max-w-xs p-8 bg-white text-stone-900 rounded-xl shadow-lg border border-stone-200">
                <span className="text-4xl font-serif font-bold text-[#581c24] italic block mb-2">
                  {page.heroCardNumber}
                </span>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-600">
                  {page.heroCardText}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tasting Types Grid */}
      <section className="py-20 bg-[#faf8f5]">
        <div className="grid-container">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {page.types.map((type, idx) => (
              <div key={idx} className="group bg-white p-6 rounded-xl border border-stone-200/80 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between">
                <div>
                  <div className="aspect-[4/3] rounded-lg overflow-hidden mb-6 shadow-sm border border-stone-100 bg-stone-100">
                    <img 
                      src={getOptimizedImgUrl(type.image, 600)} 
                      alt={type.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                    />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[11px] font-mono font-bold text-[#581c24] bg-[#581c24]/5 px-2 py-0.5 rounded border border-[#581c24]/10">
                      {type.number || `0${idx + 1}`}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-stone-500">
                      {page.tastingTypesBadge}
                    </span>
                  </div>
                  <h3 className="text-base sm:text-lg font-serif font-bold uppercase tracking-tight text-stone-950 mb-3 min-h-[3rem] flex items-center">
                    {type.title}
                  </h3>
                  <p className="text-xs text-stone-600 leading-relaxed mb-6">
                    {type.description}
                  </p>
                </div>
                <div className="pt-4 border-t border-stone-100">
                  <Link to={getRouteUrl('contact')} className="text-xs font-bold uppercase tracking-[0.2em] flex items-center text-[#581c24] group-hover:translate-x-1 transition-transform">
                    {page.requestButton} <span className="ml-2">→</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 bg-[#f4efe6] border-t border-b border-stone-200/80">
        <div className="grid-container">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-6 space-y-6">
              <span className="text-xs font-bold uppercase tracking-[0.4em] text-[#581c24] block">
                {page.philosophyBadge}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold uppercase tracking-tight text-stone-950">
                {page.philosophyTitle}
              </h2>
              <p className="text-stone-700 leading-relaxed text-sm md:text-base">
                {page.philosophyP1}
              </p>
              <p className="text-stone-700 leading-relaxed text-sm md:text-base">
                {page.philosophyP2}
              </p>
            </div>
            <div className="md:col-span-6">
              <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-lg border border-stone-200">
                <img 
                  src={getOptimizedImgUrl('https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/367f3382-a0fb-44a9-a426-19b40e26171b.jpg', 800)} 
                  alt="Wine Tasting Atmosphere" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Atmospheric Gallery Grid with Lightbox */}
      <section className="py-24 bg-[#faf8f5]">
        <div className="grid-container">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#f2ede4] rounded-full border border-stone-300">
              <Sparkles className="w-3.5 h-3.5 text-[#581c24]" />
              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#581c24]">
                {page.galleryBadge}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-serif font-bold uppercase tracking-tight text-stone-950">
              {page.galleryTitle}
            </h2>
            <p className="text-xs md:text-sm text-stone-600 uppercase tracking-widest">
              {page.gallerySubtitle}
            </p>
          </div>

          {/* Masonry / Harmonious Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {galleryImages.map((item, idx) => (
              <div 
                key={idx}
                onClick={() => openLightbox(idx)}
                className="group relative overflow-hidden rounded-xl bg-stone-100 border border-stone-200/80 shadow-sm cursor-pointer aspect-square sm:aspect-[3/4] lg:aspect-[4/5]"
              >
                <img 
                  src={getOptimizedImgUrl(item.url, 800)} 
                  alt={item.title} 
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                
                {/* Clean hover gradient with details */}
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/85 via-stone-950/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6 md:p-8">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#e8cbb6] bg-white/10 backdrop-blur-sm px-2.5 py-1 rounded-md">
                      {item.category}
                    </span>
                    <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white">
                      <Maximize2 className="w-4 h-4" />
                    </div>
                  </div>
                  <h3 className="text-xl font-serif font-bold uppercase tracking-tight text-white mb-1">
                    {item.title}
                  </h3>
                  <p className="text-xs text-stone-200 line-clamp-2">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeModalIndex !== null && (
        <div 
          className="fixed inset-0 z-[100] bg-stone-950/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10 select-none animate-fadeIn"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button 
            onClick={closeLightbox}
            className="absolute top-6 right-6 z-20 text-stone-300 hover:text-white bg-stone-900/60 p-3 rounded-full border border-stone-700 transition-colors focus:outline-none"
            aria-label="Luk visning"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Navigation Prev */}
          <button 
            onClick={prevImage}
            className="absolute left-4 md:left-8 z-20 text-stone-300 hover:text-white bg-stone-900/60 p-3 rounded-full border border-stone-700 transition-colors focus:outline-none"
            aria-label="Forrige billede"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Navigation Next */}
          <button 
            onClick={nextImage}
            className="absolute right-4 md:right-8 z-20 text-stone-300 hover:text-white bg-stone-900/60 p-3 rounded-full border border-stone-700 transition-colors focus:outline-none"
            aria-label="Næste billede"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Active Image Box */}
          <div 
            className="relative max-w-5xl max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="overflow-hidden rounded-xl border border-stone-700 bg-stone-900 shadow-2xl">
              <img 
                src={getOptimizedImgUrl(galleryImages[activeModalIndex].url, 1600)} 
                alt={galleryImages[activeModalIndex].title}
                className="max-h-[70vh] w-auto object-contain mx-auto"
              />
            </div>
            
            {/* Modal Caption */}
            <div className="mt-4 text-center max-w-xl px-4">
              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#e8cbb6] mb-1 inline-block">
                {galleryImages[activeModalIndex].category}
              </span>
              <h3 className="text-xl font-serif font-bold uppercase text-white tracking-wide">
                {galleryImages[activeModalIndex].title}
              </h3>
              <p className="text-xs text-stone-300 mt-1">
                {galleryImages[activeModalIndex].description}
              </p>
              <span className="text-[10px] text-stone-500 font-mono mt-2 block">
                {activeModalIndex + 1} / {galleryImages.length}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <section className="py-24 bg-[#f2ede4] border-t border-stone-200 text-center">
        <div className="grid-container max-w-3xl space-y-8">
          <h2 className="text-4xl md:text-6xl font-serif font-bold uppercase tracking-tight text-stone-950">
            {page.ctaTitle1} <br /> 
            <span className="text-[#581c24] italic font-light">{page.ctaTitle2}</span>
          </h2>
          <div>
            <Link to={getRouteUrl('contact')} className="btn-primary">
              {page.ctaButton} <ArrowRight className="inline-block ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
}
